import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { Leaf, Send, Plus, Globe, Trash2, Loader2, MessageSquareText, BookOpen } from "lucide-react";

const tl = (n) => (n ?? 0).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const inputCls = "w-full px-3 py-2.5 rounded-xl border border-stone-300 text-sm focus:outline-none focus:ring-2 focus:ring-forest/30 focus:border-forest";
const SAMPLES = ["Uykusuzluk", "Bağışıklık güçlendirme", "Sindirim problemi", "Eklem ağrısı", "Stres ve yorgunluk"];

export default function AdminAktarAI() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [matched, setMatched] = useState([]);
  const [kb, setKb] = useState([]);
  const [kbTitle, setKbTitle] = useState("");
  const [kbContent, setKbContent] = useState("");
  const [kbUrl, setKbUrl] = useState("");
  const [kbBusy, setKbBusy] = useState(false);
  const chatEnd = useRef(null);

  const loadKb = async () => {
    try {
      const { data } = await api.get("/admin/aktarai/knowledge");
      setKb(data || []);
    } catch { /* sessiz */ }
  };
  useEffect(() => { loadKb(); }, []);
  useEffect(() => { chatEnd.current?.scrollIntoView({ behavior: "smooth" }); }, [messages, busy]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || busy) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setBusy(true);
    try {
      const { data } = await api.post("/admin/aktarai/chat", { message: msg, history: messages.slice(-8) });
      setMessages((m) => [...m, { role: "assistant", text: data.reply }]);
      setMatched(data.products || []);
    } catch (e) {
      const det = e?.response?.data?.detail || "Cevap alınamadı";
      setMessages((m) => [...m, { role: "assistant", text: `Hata: ${det}` }]);
    } finally { setBusy(false); }
  };

  const addText = async () => {
    if (kbTitle.trim().length < 2 || kbContent.trim().length < 10) { toast.error("Başlık ve içerik (en az 10 karakter) gerekli"); return; }
    setKbBusy(true);
    try {
      await api.post("/admin/aktarai/knowledge", { title: kbTitle.trim(), content: kbContent.trim() });
      toast.success("Bilgi bankasına eklendi");
      setKbTitle(""); setKbContent("");
      loadKb();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eklenemedi"); }
    finally { setKbBusy(false); }
  };

  const addUrl = async () => {
    if (!kbUrl.trim()) { toast.error("Sayfa adresi girin"); return; }
    setKbBusy(true);
    try {
      await api.post("/admin/aktarai/knowledge/url", { url: kbUrl.trim() });
      toast.success("Web sayfası bilgi bankasına eklendi");
      setKbUrl("");
      loadKb();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sayfa eklenemedi"); }
    finally { setKbBusy(false); }
  };

  const delKb = async (k) => {
    if (!window.confirm(`"${k.title}" bilgi bankasından silinsin mi?`)) return;
    try {
      await api.delete(`/admin/aktarai/knowledge/${k.kid}`);
      toast.success("Kayıt silindi");
      loadKb();
    } catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  return (
    <div data-testid="aktarai-page" className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-stone-800 flex items-center gap-2"><Leaf className="w-6 h-6 text-forest" /> AktarAI Asistan</h1>
        <p className="text-sm text-stone-500 mt-1">Yalnızca sizin yüklediğiniz ürün bilgileriyle cevap verir; internet araştırması yapmaz. Şikayet yazın, ilgili ürünleri listeleyin.</p>
      </div>

      <div className="grid lg:grid-cols-3 gap-5 items-start">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-stone-200 flex flex-col">
          <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-2">
            <MessageSquareText className="w-4 h-4 text-forest" />
            <p className="text-sm font-semibold text-stone-700">Sohbet</p>
            {messages.length > 0 && (
              <button data-testid="aktarai-clear" onClick={() => { setMessages([]); setMatched([]); toast.success("Sohbet temizlendi"); }}
                className="ml-auto px-2.5 py-1.5 rounded-lg border border-stone-200 text-xs font-semibold text-stone-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors flex items-center gap-1">
                <Trash2 className="w-3.5 h-3.5" /> Temizle
              </button>
            )}
            <span className={`text-xs text-stone-400 ${messages.length ? "" : "ml-auto"}`}>{kb.length} bilgi kaydı yüklü</span>
          </div>

          <div data-testid="aktarai-msg-list" className="flex-1 min-h-[380px] max-h-[55vh] overflow-y-auto p-4 space-y-3">
            {messages.length === 0 && (
              <div className="text-center py-10 text-stone-400 text-sm">
                <Leaf className="w-8 h-8 mx-auto mb-2 text-stone-300" />
                {kb.length ? "Bir şikayet veya ürün adı yazın — örn. \"uykusuzluk\"" : "Önce sağ panelden ürün bilgisi yükleyin (metin veya web sayfası)."}
                <div className="flex flex-wrap justify-center gap-2 mt-4">
                  {SAMPLES.map((s) => (
                    <button key={s} data-testid={`aktarai-sample-${s}`} onClick={() => kb.length && send(s)} disabled={!kb.length}
                      className="px-3 py-1.5 rounded-full bg-forest/10 text-forest text-xs font-semibold hover:bg-forest/20 transition-colors disabled:opacity-40">{s}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} data-testid={`aktarai-msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-forest text-white" : "bg-stone-100 text-stone-800"}`}>{m.text}</div>
              </div>
            ))}
            {busy && <div className="flex items-center gap-2 text-stone-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> AktarAI düşünüyor...</div>}
            <div ref={chatEnd} />
          </div>

          {matched.length > 0 && (
            <div data-testid="aktarai-matched-products" className="border-t border-stone-100 p-3">
              <p className="text-xs font-semibold text-stone-600 mb-2">Katalogda eşleşen ürünler ({matched.length})</p>
              <div className="flex flex-wrap gap-2">
                {matched.map((p) => (
                  <Link key={p.product_id} to={`/urun/${p.slug}`} target="_blank" data-testid={`aktarai-product-${p.product_id}`}
                    className="flex items-center gap-2 px-3 py-2 rounded-xl border border-stone-200 hover:border-forest hover:bg-forest/5 transition-colors">
                    {p.images?.[0] && <img src={p.images[0]} alt="" className="w-8 h-8 rounded-lg object-cover" />}
                    <span>
                      <span className="block text-xs font-semibold text-stone-800">{p.name}</span>
                      <span className="block text-[10px] text-stone-400">{tl(p.discount_price ?? p.price)} ₺ • stok: {p.stock} {p.base_unit || p.unit || "adet"}</span>
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          <div className="border-t border-stone-100 p-3 flex gap-2">
            <input data-testid="aktarai-input" className={inputCls} placeholder="Şikayet, ürün adı veya soru yazın... (örn. uykusuzluk)"
              value={input} onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()} />
            <button data-testid="aktarai-send" onClick={() => send()} disabled={busy || input.trim().length < 2}
              className="px-4 py-2.5 rounded-xl bg-forest text-white font-semibold text-sm hover:bg-forest/90 transition-colors disabled:opacity-40 shrink-0">
              <Send className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-stone-200">
          <div className="px-4 py-3 border-b border-stone-100 flex items-center gap-2">
            <BookOpen className="w-4 h-4 text-forest" />
            <p className="text-sm font-semibold text-stone-700">Bilgi Bankası</p>
          </div>
          <div className="p-4 space-y-4">
            <div className="space-y-2">
              <p className="text-xs font-semibold text-stone-600">Metin olarak ekle</p>
              <input data-testid="kb-title" className={inputCls} placeholder="Başlık — örn. Anzer Balı" value={kbTitle} onChange={(e) => setKbTitle(e.target.value)} />
              <textarea data-testid="kb-content" rows={5} className={inputCls}
                placeholder={"Ürün açıklaması, hangi şikayetlere iyi geldiği, kimlerin kullanabileceği / kullanmaması gerektiği..."}
                value={kbContent} onChange={(e) => setKbContent(e.target.value)} />
              <button data-testid="kb-add" onClick={addText} disabled={kbBusy}
                className="w-full py-2.5 rounded-xl bg-forest text-white text-sm font-semibold hover:bg-forest/90 transition-colors disabled:opacity-40 flex items-center justify-center gap-2">
                <Plus className="w-4 h-4" /> Bilgiyi Kaydet
              </button>
            </div>
            <div className="space-y-2 pt-2 border-t border-stone-100">
              <p className="text-xs font-semibold text-stone-600">Web sayfasından ekle</p>
              <div className="flex gap-2">
                <input data-testid="kb-url" className={inputCls} placeholder="https://..." value={kbUrl} onChange={(e) => setKbUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && addUrl()} />
                <button data-testid="kb-url-add" onClick={addUrl} disabled={kbBusy}
                  className="px-3 py-2.5 rounded-xl border border-forest text-forest hover:bg-forest/5 transition-colors disabled:opacity-40 shrink-0" title="Sayfayı bilgi bankasına ekle">
                  <Globe className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div data-testid="kb-list" className="pt-2 border-t border-stone-100 space-y-1.5 max-h-64 overflow-y-auto">
              {kb.map((k) => (
                <div key={k.kid} data-testid={`kb-entry-${k.kid}`} className="flex items-center gap-2 px-3 py-2 rounded-xl bg-stone-50 border border-stone-100">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-stone-800 truncate">{k.title}</p>
                    <p className="text-[10px] text-stone-400">{k.source === "url" ? "Web sayfası" : "Metin"} • {(k.created_at || "").slice(0, 10)} • {k.content.length} karakter</p>
                  </div>
                  <button data-testid={`kb-del-${k.kid}`} onClick={() => delKb(k)} className="p-1.5 hover:bg-red-50 rounded-lg shrink-0" title="Sil">
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </button>
                </div>
              ))}
              {kb.length === 0 && <p className="text-xs text-stone-400 text-center py-4">Henüz bilgi kaydı yok</p>}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
